import {z} from 'zod';

/**
 * Field on a prospective section leader's own application document
 * (`users/{uid}/{courseId}/sectionLeaderApplication`) that holds the open
 * section times they said they could teach. The applicant may write every
 * field of that document except server-set ones, and admins and senior SLs
 * may read it, so no extra Firestore rule is needed.
 */
export const OPEN_SLOT_AVAILABILITY_FIELD = 'openSlotAvailability';

/**
 * The open section times a prospective section leader could teach, as saved
 * from the open-sections application page.
 */
export const OpenSlotAvailabilitySchema = z.object({
  /**
   * Every open `timeIndex` the applicant ticked. Only times that were open
   * when they saved; see `openSectionLeaderSlots` on the course document.
   */
  timeIndexes: z.array(z.number().int().nonnegative()),
  /** When they saved, in epoch milliseconds of server time. */
  updatedAtMs: z.number(),
});

/** The open section times a prospective section leader could teach. */
export type OpenSlotAvailability = z.infer<typeof OpenSlotAvailabilitySchema>;

/**
 * Reads the saved open-slot availability from a section leader application
 * document's data.
 *
 * @param applicationData - The raw application document data. When omitted or
 *   when the field is missing or malformed, nothing was saved.
 * @return The saved availability, or `undefined` when there is none.
 * @throws Never.
 */
export function parseOpenSlotAvailability(
  applicationData: Record<string, unknown> | undefined,
): OpenSlotAvailability | undefined {
  const result = OpenSlotAvailabilitySchema.safeParse(
    applicationData?.[OPEN_SLOT_AVAILABILITY_FIELD],
  );
  return result.success ? result.data : undefined;
}
